const META_ROUTE_TOKEN_PATTERN = /--[a-z0-9-]+/gi;
const EXACT_PATTERN_PREFIX = 'exact:';
const DYNAMIC_ROUTE_RESERVED_SEGMENTS_BY_PARENT = new Map<string, Set<string>>([
  ['/school/faculty', new Set(['directory', 'profile'])],
]);

export const normalizeAssistantRouteIdentity = (value: string | null | undefined): string => {
  const raw = String(value ?? '').trim();
  if (!raw) {
    return '/';
  }
  const asPathname = raw.startsWith('http://') || raw.startsWith('https://')
    ? new URL(raw).pathname
    : raw;
  const withLeadingSlash = asPathname.startsWith('/') ? asPathname : `/${asPathname}`;
  const withoutQuery = withLeadingSlash.split('?')[0] ?? withLeadingSlash;
  const collapsed = withoutQuery.replace(/\/{2,}/g, '/');
  const normalized = collapsed
    .split('/')
    .map((segment) => segment.replace(META_ROUTE_TOKEN_PATTERN, ''))
    .join('/') || '/';
  if (normalized.length > 1 && normalized.endsWith('/')) {
    return normalized.slice(0, -1).toLowerCase();
  }
  return normalized.toLowerCase();
};

const splitRouteSegments = (value: string): string[] => (
  normalizeAssistantRouteIdentity(value)
    .split('/')
    .filter(Boolean)
);

export const stripAssistantRoutePatternQualifier = (value: string | null | undefined): string => {
  const raw = String(value ?? '').trim();
  return raw.startsWith(EXACT_PATTERN_PREFIX)
    ? raw.slice(EXACT_PATTERN_PREFIX.length).trim()
    : raw;
};

const isExactRoutePattern = (value: string | null | undefined): boolean =>
  String(value ?? '').trim().startsWith(EXACT_PATTERN_PREFIX);

const isDescendantOnlyRoutePattern = (value: string | null | undefined): boolean =>
  stripAssistantRoutePatternQualifier(value).endsWith('/');

const patternHasDynamicSegments = (pattern: string): boolean => (
  splitRouteSegments(pattern).some((segment) => segment.startsWith(':'))
);

export const isAssistantRouteTemplateSegment = (segment: string | null | undefined): boolean => {
  const raw = String(segment ?? '').trim();
  let normalized = raw;
  try {
    normalized = decodeURIComponent(raw);
  } catch {
    normalized = raw;
  }
  if (!normalized) {
    return false;
  }
  return (
    normalized.startsWith(':') ||
    (/^\{[^/]+\}$/.test(normalized)) ||
    (/^\[[^/]+\]$/.test(normalized))
  );
};

export const isAssistantRouteTemplatePathname = (value: string | null | undefined): boolean => (
  splitRouteSegments(String(value ?? '')).some(isAssistantRouteTemplateSegment)
);

const isReservedDynamicRouteValue = (
  patternSegments: string[],
  segmentIndex: number,
  pathSegment: string,
): boolean => {
  const parentPath = `/${patternSegments.slice(0, segmentIndex).join('/')}`;
  const reservedSegments = DYNAMIC_ROUTE_RESERVED_SEGMENTS_BY_PARENT.get(parentPath);
  return reservedSegments?.has(pathSegment) === true;
};

export const routeMatchesAssistantPattern = (
  pathname: string | null | undefined,
  pattern: string | null | undefined,
): boolean => {
  const normalizedPattern = normalizeAssistantRouteIdentity(stripAssistantRoutePatternQualifier(pattern));
  const normalizedPath = normalizeAssistantRouteIdentity(pathname);

  if (!normalizedPattern || normalizedPattern === '/') {
    return normalizedPath === '/';
  }

  if (isExactRoutePattern(pattern)) {
    return normalizedPath === normalizedPattern;
  }

  if (!patternHasDynamicSegments(normalizedPattern)) {
    if (isDescendantOnlyRoutePattern(pattern)) {
      return normalizedPath.startsWith(`${normalizedPattern}/`);
    }
    return normalizedPath === normalizedPattern || normalizedPath.startsWith(`${normalizedPattern}/`);
  }

  const pathSegments = splitRouteSegments(normalizedPath);
  const patternSegments = splitRouteSegments(normalizedPattern);

  if (pathSegments.length !== patternSegments.length) {
    return false;
  }

  for (let index = 0; index < patternSegments.length; index += 1) {
    const patternSegment = patternSegments[index] ?? '';
    const pathSegment = pathSegments[index] ?? '';
    if (patternSegment.startsWith(':')) {
      if (
        !pathSegment ||
        isAssistantRouteTemplateSegment(pathSegment) ||
        isReservedDynamicRouteValue(patternSegments, index, pathSegment)
      ) {
        return false;
      }
      continue;
    }
    if (patternSegment !== pathSegment) {
      return false;
    }
  }
  return true;
};

export const routeMatchesAnyAssistantPattern = (
  pathname: string | null | undefined,
  patterns: string[],
): boolean => {
  if (patterns.length === 0) {
    return true;
  }
  return patterns.some((pattern) => routeMatchesAssistantPattern(pathname, pattern));
};
